import { Box, Text } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { SingleValue } from "react-select";
import { isSuccess } from "../../../../../../../common/helper/commonHelper";
import { INCLUSION_LIST_TYPES } from "../../../../../../../common/types/campaign";
import {
  AssetField,
  SalesforceConnection,
} from "../../../../../../../common/types/connection";
import DropdownWithSearch from "../../../../../../../components/DropdownWithSearch";
import {
  CustomAssetBadgeOption,
  CustomSingleValue,
} from "../../../../../../../components/OptionHelper";
import { useAppDispatch } from "../../../../../../../store";
import {
  listAllDynamicLists,
  selectCampaign,
} from "../../../../../campaign/campaignSlice";
import {
  listStaticListSummaryList,
  selectStaticList,
} from "../../../../../staticlist/staticlistSlice";
import {
  removeInclusionList,
  updateInclusionList,
} from "../../../../connectionSlice";
import { Header } from "./Header";
import { NoteBox } from "./NoteBox";

export default function InflectionToSalesforceInclusionList({
  data,
}: {
  data: SalesforceConnection;
}) {
  const dispatch = useAppDispatch();

  const { dynamicListAll } = useSelector(selectCampaign);
  const { staticListSummaryList } = useSelector(selectStaticList);

  useEffect(() => {
    dispatch(listAllDynamicLists());
    dispatch(listStaticListSummaryList());
  }, [dispatch]);

  const campaignOptions: AssetField[] = useMemo(
    () =>
      isSuccess(dynamicListAll.loading)
        ? dynamicListAll.data.map((item) => {
            return {
              name: item.name,
              id: item.dynamic_list_id,
              type: INCLUSION_LIST_TYPES.DYNAMIC,
            };
          })
        : [],
    [dynamicListAll]
  );

  const staticListOptions: AssetField[] = useMemo(
    () =>
      isSuccess(staticListSummaryList.loading)
        ? staticListSummaryList.data.map((item) => {
            return {
              name: item.name,
              id: item.static_list_id,
              type: INCLUSION_LIST_TYPES.STATIC,
            };
          })
        : [],
    [staticListSummaryList]
  );

  const connectionId = data.connection_id;

  const onValueChange = (val: SingleValue<AssetField>) => {
    if (val && val.id) {
      const inclusionListData = {
        connectionId,
        inclusionList: {
          list_id: val.id,
          list_type: val.type as INCLUSION_LIST_TYPES,
        },
      };
      dispatch(updateInclusionList(inclusionListData));
    } else {
      dispatch(removeInclusionList(connectionId));
    }
  };

  return (
    <Box>
      <Header
        title="Limiting what syncs"
        desc="An Inflection contact must be on the inclusion list to sync to Salesforce"
      />

      <Box mt="2">
        <NoteBox text="If no list is selected, all Inflection contacts would be synced to Salesforce" />
      </Box>

      <Text mt="3">Select an inclusion list</Text>

      <Box pt="3" width="300px">
        <DropdownWithSearch
          options={[...campaignOptions, ...staticListOptions]}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          value={
            [...campaignOptions, ...staticListOptions].find(
              (option) => option.id === (data.inclusion_list?.list_id ?? null)
            ) ?? null
          }
          onChange={(val: SingleValue<AssetField>) => onValueChange(val)}
          components={{
            Option: CustomAssetBadgeOption,
            SingleValue: CustomSingleValue,
          }}
          controlStyle={{
            width: "300px",
          }}
          isClearable={true}
        />
      </Box>
    </Box>
  );
}
